import React, { useEffect, useState, useMemo } from "react";
import {
  FaInbox, FaChartLine, FaChartPie, FaChartBar,
  FaUsers, FaWallet, FaArrowUp, FaArrowDown, FaMoneyBillWave,
  FaHandHoldingUsd, FaExclamationCircle
} from "react-icons/fa";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Line, Pie, Bar } from "react-chartjs-2";
import { getMonthName } from "../../utils/monthUtils";
import axiosInstance from "../../utils/axiosConfig";
import LoadingSpinner from "../atoms/LoadingSpinner";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface FinanceData {
  totalEmployees: number;
  basicSalary: {
    total: number;
    average: number;
  };
  grossSalary: {
    total: number;
    average: number;
  };
  netSalary: {
    total: number;
    average: number;
  };
  allowances: {
    total: number;
    average: number;
  };
  deductions: {
    total: number;
    average: number;
    breakdown: {
      tax: number;
      eobi: number;
      employeePF: number;
      employerPF: number;
      lateIns: number;
      absents: number;
      halfDays: number;
    };
  };
  extraPayments: number;
  attendance: {
    totalAbsentDays: number;
    totalHalfDays: number;
    totalLateIns: number;
  };
  metrics: {
    averageDeductionPercentage: number;
    averageNetToGrossRatio: number;
    averageTaxPercentage: number;
  };
}

interface MonthlyBreakdown {
  _id: {
    year: number;
    month: number;
  };
  totalGrossSalary: number;
  totalNetSalary: number;
  totalDeductions: number;
  employeeCount: number;
}

const PayrollFinanceAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [financeData, setFinanceData] = useState<FinanceData | null>(null);
  const [monthlyBreakdown, setMonthlyBreakdown] = useState<MonthlyBreakdown[]>([]);
  const [filter, setFilter] = useState<string>("last_six_months");

  const COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#64748b", "#ec4899"];
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchFinanceData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${backendUrl}/api/payroll/finance`
      );
      const data = response.data;
      setFinanceData(data.data);
      if (data.monthlyBreakdown) {
        setMonthlyBreakdown(data.monthlyBreakdown);
      }
    } catch (error) {
      console.error("Error fetching finance data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinanceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const filteredMonthlyData = useMemo(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    let filtered = [...monthlyBreakdown].sort((a, b) => {
      if (a._id.year !== b._id.year) return a._id.year - b._id.year;
      return a._id.month - b._id.month;
    });

    switch (filter) {
      case "this_month":
        const currentMonth = currentDate.getMonth() + 1;
        filtered = filtered.filter(item => item._id.year === currentYear && item._id.month === currentMonth);
        break;
      case "last_six_months":
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        filtered = filtered.filter(item => {
          const itemDate = new Date(item._id.year, item._id.month - 1);
          return itemDate >= sixMonthsAgo;
        });
        break;
      case "this_year":
        filtered = filtered.filter(item => item._id.year === currentYear);
        break;
      default:
        break;
    }
    return filtered;
  }, [monthlyBreakdown, filter]);

  const trends = useMemo(() => {
    if (monthlyBreakdown.length < 2) return null;
    const sorted = [...monthlyBreakdown].sort((a, b) => {
      if (a._id.year !== b._id.year) return a._id.year - b._id.year;
      return a._id.month - b._id.month;
    });
    const latest = sorted[sorted.length - 1];
    const previous = sorted[sorted.length - 2];

    const grossChange = ((latest.totalGrossSalary - previous.totalGrossSalary) / previous.totalGrossSalary) * 100;
    const netChange = ((latest.totalNetSalary - previous.totalNetSalary) / previous.totalNetSalary) * 100;

    return { grossChange, netChange, latest, previous };
  }, [monthlyBreakdown]);

  const getMonthlyTrendsData = () => {
    const labels = filteredMonthlyData.map(
      (item) => `${getMonthName(item._id.month)} ${item._id.year}`
    );

    return {
      labels,
      datasets: [
        {
          label: "Gross Expenditure",
          data: filteredMonthlyData.map((item) => item.totalGrossSalary),
          borderColor: "#6366f1", // Indigo-500
          backgroundColor: "rgba(99, 102, 241, 0.1)",
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: "Net Disbursement",
          data: filteredMonthlyData.map((item) => item.totalNetSalary),
          borderColor: "#10b981", // Emerald-500
          backgroundColor: "rgba(16, 185, 129, 0.1)",
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  const getDeductionsData = () => {
    if (!financeData) return { labels: [], datasets: [] };

    const breakdown = financeData.deductions.breakdown;
    const labels = ["Tax", "EOBI", "Employee PF", "Employer PF", "Late Ins", "Absents", "Half Days"];
    const values = [
      breakdown.tax,
      breakdown.eobi,
      breakdown.employeePF,
      breakdown.employerPF,
      breakdown.lateIns,
      breakdown.absents,
      breakdown.halfDays
    ];

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: COLORS,
          hoverOffset: 20,
          borderWidth: 0,
        },
      ],
    };
  };

  const getMetricsData = () => ({
    labels: ["Efficiency (Net/Gross)", "Burn Rate (Deductions)", "Compliance (Tax)"],
    datasets: [
      {
        label: "Benchmark (%)",
        data: [
          financeData?.metrics.averageNetToGrossRatio || 0,
          financeData?.metrics.averageDeductionPercentage || 0,
          financeData?.metrics.averageTaxPercentage || 0,
        ],
        backgroundColor: ["#10b981", "#ef4444", "#3b82f6"],
        borderRadius: 8,
        barThickness: 40,
      },
    ],
  });

  if (loading) {
    return <LoadingSpinner className="h-[600px]" size="lg" text="Analyzing Financial Data..." />;
  }

  if (!financeData) {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-white rounded-2xl border border-platinum-200">
        <FaInbox className="text-5xl text-platinum-300 mb-4 animate-pulse" />
        <p className="text-slate-grey-500 font-medium">No payroll records found to analyze.</p>
        <button
          onClick={fetchFinanceData}
          className="mt-4 text-gunmetal-600 font-bold hover:underline"
        >
          Retry Calibration
        </button>
      </div>
    );
  }

  return (
    <div className="w-full space-y-8 mb-12">
      {/* Top Navigation & Title */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gunmetal-900 rounded-2xl flex items-center justify-center shadow-lg shadow-gunmetal-900/20">
            <FaChartLine className="text-platinum-50 text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gunmetal-900 uppercase tracking-tighter">
              Financial Intelligence
            </h1>
            <p className="text-slate-grey-500 font-medium">
              Data-driven insights into organization's capital disbursement.
            </p>
          </div>
        </div>

        <div className="flex bg-platinum-100 p-1 rounded-xl items-center shadow-inner">
          {["this_month", "last_six_months", "this_year"].map((p) => (
            <button
              key={p}
              onClick={() => setFilter(p)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-lg transition-all duration-300 ${filter === p
                ? "bg-white text-gunmetal-900 shadow-sm ring-1 ring-black/5"
                : "text-slate-grey-500 hover:text-gunmetal-600"
                }`}
            >
              {p.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>

      {/* Core KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          label="Cumulative Disbursement"
          value={formatCurrency(financeData.grossSalary.total)}
          subValue={`Avg: ${formatCurrency(financeData.grossSalary.average)}`}
          icon={<FaWallet className="text-indigo-600" />}
          trend={trends?.grossChange}
          color="indigo"
        />
        <StatCard
          label="Net Operating Payout"
          value={formatCurrency(financeData.netSalary.total)}
          subValue={`Avg: ${formatCurrency(financeData.netSalary.average)}`}
          icon={<FaMoneyBillWave className="text-emerald-600" />}
          trend={trends?.netChange}
          color="emerald"
        />
        <StatCard
          label="Recovered Deductions"
          value={formatCurrency(financeData.deductions.total)}
          subValue={`${financeData.metrics.averageDeductionPercentage}% of Gross`}
          icon={<FaHandHoldingUsd className="text-amber-600" />}
          color="amber"
        />
        <StatCard
          label="Active Talent Pool"
          value={financeData.totalEmployees.toString()}
          subValue="Cross-departmental"
          icon={<FaUsers className="text-blue-600" />}
          color="blue"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-3xl border border-platinum-200 p-8 shadow-sm">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-lg font-bold text-gunmetal-900 flex items-center gap-2">
              <FaChartLine className="text-indigo-500" /> Spending Dynamics
            </h3>
            <div className="flex items-center gap-4 text-xs font-bold uppercase text-slate-grey-500">
              <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-indigo-500"></span> Gross</div>
              <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500"></span> Net</div>
            </div>
          </div>
          <div className="h-[350px]">
            <Line data={getMonthlyTrendsData()} options={chartOptions} />
          </div>
        </div>

        {/* Breakdown Card */}
        <div className="bg-white rounded-3xl border border-platinum-200 p-8 shadow-sm flex flex-col">
          <h3 className="text-lg font-bold text-gunmetal-900 mb-6 flex items-center gap-2">
            <FaChartPie className="text-emerald-500" /> Deduction Velocity
          </h3>
          <div className="flex-1 relative flex items-center justify-center">
            <div className="w-full h-[280px]">
              <Pie data={getDeductionsData()} options={pieOptions} />
            </div>
          </div>
          <div className="mt-8 space-y-3">
            <BreakdownItem label="Total Deductions" value={formatCurrency(financeData.deductions.total)} color="emerald-500" />
            <BreakdownItem label="Extra Payouts" value={formatCurrency(financeData.extraPayments)} color="blue-500" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Benchmark Chart */}
        <div className="bg-white rounded-3xl border border-platinum-200 p-8 shadow-sm">
          <h3 className="text-lg font-bold text-gunmetal-900 mb-8 flex items-center gap-2">
            <FaChartBar className="text-amber-500" /> Organizational Benchmarks
          </h3>
          <div className="h-[280px]">
            <Bar data={getMetricsData()} options={barOptions} />
          </div>
        </div>

        {/* Insights & Actions */}
        <div className="bg-gunmetal-900 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <h3 className="text-xl font-bold mb-6 flex items-center gap-2 relative z-10">
            <FaChartBar className="text-platinum-400" /> Strategic Outlook
          </h3>

          <div className="space-y-6 relative z-10">
            <InsightItem
              icon={<FaChartLine />}
              title="Budgetary Trend"
              desc={trends && trends.grossChange > 0
                ? `Expenditure increased by ${trends.grossChange.toFixed(1)}% compared to the previous period.`
                : "Budget remains stable compared to previous analysis cycles."}
            />
            <InsightItem
              icon={<FaExclamationCircle />}
              title="Asset Burn"
              desc={`${financeData.attendance.totalAbsentDays} unauthorized absences this period resulted in ${formatCurrency(financeData.deductions.breakdown.absents)} in recovered capital.`}
              color="text-amber-400"
            />
            <InsightItem
              icon={<FaHandHoldingUsd />}
              title="Capital Efficiency"
              desc={`Organization is maintaining a ${financeData.metrics.averageNetToGrossRatio}% net payout efficiency, aligning with top-tier benchmarks.`}
              color="text-emerald-400"
            />
          </div>

          <div className="mt-8 p-4 bg-white/10 rounded-2xl border border-white/10 flex items-center gap-4 group cursor-pointer hover:bg-white/20 transition-all">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-gunmetal-900 font-bold shadow-lg">?</div>
            <div>
              <p className="text-sm font-bold uppercase tracking-wider text-platinum-400">Financial Advisory</p>
              <p className="text-xs text-platinum-300">Run projection models for next quarter?</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string;
  subValue: string;
  icon: React.ReactNode;
  trend?: number;
  color: string;
}> = ({ label, value, subValue, icon, trend, color }) => (
  <div className="bg-white p-7 rounded-3xl border border-platinum-200 shadow-sm hover:shadow-md transition-all duration-300 group">
    <div className="flex justify-between items-start mb-6">
      <div className={`w-12 h-12 bg-${color}-50 rounded-2xl flex items-center justify-center text-xl transition-all group-hover:scale-110 shadow-sm`}>
        {icon}
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${trend > 0 ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>
          {trend > 0 ? <FaArrowUp /> : <FaArrowDown />}
          {Math.abs(trend).toFixed(1)}%
        </div>
      )}
    </div>
    <div className="space-y-1">
      <p className="text-xs font-bold text-slate-grey-400 uppercase tracking-widest leading-none">{label}</p>
      <h3 className="text-2xl font-black text-gunmetal-900 tracking-tighter">{value}</h3>
      <p className="text-xs font-medium text-slate-grey-500 truncate">{subValue}</p>
    </div>
  </div>
);

const BreakdownItem: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <div className="flex justify-between items-center py-2 border-b border-platinum-100 last:border-0">
    <span className="text-sm font-medium text-slate-grey-500 flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full bg-${color}`}></span> {label}
    </span>
    <span className="text-sm font-bold text-gunmetal-900 font-mono">{value}</span>
  </div>
);

const InsightItem: React.FC<{ icon: React.ReactNode; title: string; desc: string; color?: string }> = ({ icon, title, desc, color = "text-platinum-50" }) => (
  <div className="flex gap-4">
    <div className={`mt-1 text-lg ${color}`}>{icon}</div>
    <div>
      <h4 className="text-sm font-bold tracking-wide uppercase text-platinum-400 mb-1">{title}</h4>
      <p className="text-sm text-platinum-100 font-medium leading-relaxed">{desc}</p>
    </div>
  </div>
);

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "#0f172a",
      titleFont: { family: "'Inter', sans-serif", weight: 'bold' as 'bold' },
      bodyFont: { family: "'Inter', sans-serif" },
      padding: 12,
      cornerRadius: 12,
      displayColors: true,
    }
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#64748b", font: { family: "'Inter', sans-serif", size: 10 } }
    },
    y: {
      border: { display: false },
      grid: { color: "#f1f5f8" },
      ticks: {
        color: "#64748b",
        font: { family: "'Inter', sans-serif", size: 10 },
        callback: (value: any) => `PKR ${value / 1000}k`
      }
    }
  }
};

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 20,
        font: { family: "'Inter', sans-serif", size: 11, weight: 'bold' as 'bold' },
        color: "#475569"
      }
    },
    tooltip: {
      backgroundColor: "#0f172a",
      padding: 12,
      cornerRadius: 12,
    }
  }
};

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "#0f172a",
      bodyFont: { family: "'Inter', sans-serif" },
      padding: 12,
      cornerRadius: 12,
    }
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#64748b", font: { family: "'Inter', sans-serif", size: 9, weight: 'bold' as 'bold' } }
    },
    y: {
      beginAtZero: true,
      max: 100,
      border: { display: false },
      grid: { color: "#f1f5f8" },
      ticks: {
        color: "#64748b",
        font: { family: "'Inter', sans-serif", size: 10 },
        callback: (value: any) => `${value}%`
      }
    }
  }
};

export default PayrollFinanceAnalytics;
